import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, DropdownButton, MenuItem } from 'react-bootstrap';
import LoaderButton from '../components/LoaderButton';
import {
  authorizeCompanyCam,
  removeCCCapProject,
  getUserIntegrations,
  getCaps
} from '../utils/bedrockAPI';
import { getUser } from '../utils/Common';
import MonitorSelectionModal from '../components/MonitorSelectionModal';
import companyCamLogo from '../images/company_cam.png';
import './Integrations.css';

export default function Integrations() {
  const [userIntegrations, setUserIntegrations] = useState(null);
  const [newConnection, setNewConnection] = useState(false);
  const [capConnection, setCapConnection] = useState(null);
  const [projectConnection, setProjectConnection] = useState(null);
  const [showMonitorSelectionWindow, setShowMonitorSelectionWindow] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [capsById, setCapsById] = useState(null);
  const [caps, setCaps] = useState(null);
  const componentMounted = useRef(true);

  async function fetchUserIntegrations(userId) {
    const tempUserIntegrations = await getUserIntegrations(userId);
    if (componentMounted.current) {
      setUserIntegrations(tempUserIntegrations);
    }
  }

  useEffect(() => {
    async function loadCaps() {
      const tempCaps = await getCaps();
      if (componentMounted.current) setCaps(tempCaps);
    }
    const user = getUser();
    if (user) {
      loadCaps();
      fetchUserIntegrations(user.userId);
    }
    return () => { componentMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!showMonitorSelectionWindow) {
      fetchUserIntegrations(getUser().userId);
    }
  }, [showMonitorSelectionWindow]);

  useEffect(() => {
    if (caps) {
      const tempCapsById = {};
      for (let i = 0; i < caps.length; i++) {
        tempCapsById[caps[i].capid] = caps[i];
      }
      setCapsById(tempCapsById);
    }
  }, [caps]);

  function findProjectById(projects, projectId) {
    if (projects && projects.length > 0) {
      for (let i = 0; i < projects.length; i++) {
        if (projects[i].id === projectId) {
          return projects[i];
        }
      }
    }
    return null;
  }

  function addUserIntegration(e) {
    e.preventDefault();
    setShowMonitorSelectionWindow(true);
    setNewConnection(false);
  }

  async function deleteCapProject(e, capProject) {
    e.preventDefault();
    if (capProject && userIntegrations?.length > 0 && userIntegrations[0].integration) {
      await removeCCCapProject(userIntegrations[0], capProject);
      fetchUserIntegrations(getUser().userId);
    }
  }

  const integration = userIntegrations?.[0];
  const isCompanyCamConnected = integration?.name === 'CompanyCam';
  const capProjects = integration?.integration?.cap_projects || {};
  const projects = integration?.integration?.projects || [];
  const hasActiveProjects = projects.length > 0 && !projects.every(p => p.status === 'deleted');
  const capProjectKeys = Object.keys(capProjects);

  return (
    <div className="integrations-page">
      <header className="integrations-header">
        <nav className="brand-breadcrumb">
          <Link to="/dashboard">Dashboard</Link>
          <span className="brand-breadcrumb-sep">/</span>
          <Link to="/account/info">My Account</Link>
          <span className="brand-breadcrumb-sep">/</span>
          <span className="brand-breadcrumb-current">Integrations</span>
        </nav>
        <h1 className="integrations-title">Integrations</h1>
        <p className="integrations-subtitle">
          Connect Bedrock camera systems to third-party services.
        </p>
      </header>

      <section className="integrations-panel">
        <div className="integrations-provider">
          <img className="integrations-logo" src={companyCamLogo} alt="Company Cam" />
          <div className="integrations-provider-actions">
            {isCompanyCamConnected ? (
              <Button className="btn-brand-outline btn-compact integrations-connected-btn" disabled>
                <i className="fas fa-check-circle" /> Connected
              </Button>
            ) : (
              <LoaderButton
                className="btn-brand-dark btn-compact"
                isLoading={isLoading}
                href={authorizeCompanyCam()}
              >
                Connect Daily Feed to Company Cam
              </LoaderButton>
            )}
          </div>
        </div>

        {isCompanyCamConnected && capProjectKeys.length > 0 && (
          <div className="integrations-connections">
            <h2 className="integrations-section-title">Synced cameras</h2>
            <div className="integrations-table-wrap">
              <table className="integrations-table">
                <thead>
                  <tr>
                    <th>Bedrock CAP</th>
                    <th>Company Cam project</th>
                    <th className="integrations-table-actions-col">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {capProjectKeys.map((capProject) => (
                    <tr key={capProject}>
                      <td>{capsById?.[capProject]?.name || capProject}</td>
                      <td>
                        {findProjectById(projects, capProjects[capProject].project_id)?.name || '—'}
                      </td>
                      <td className="integrations-table-actions-col">
                        <Button
                          className="btn-brand-outline btn-compact integrations-btn-danger"
                          onClick={(e) => deleteCapProject(e, capProject)}
                          title="Remove connection"
                        >
                          <i className="fas fa-times" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {newConnection && (
          <div className="integrations-new-connection">
            <DropdownButton
              title={capConnection && capsById?.[capConnection] ? capsById[capConnection].name : 'Select a CAP'}
              id="cap-list"
              className="integrations-dropdown"
              onSelect={(key) => setCapConnection(key)}
            >
              {caps?.filter(cap => cap.status === 'ACTIVE').map(cap => (
                <MenuItem key={cap.capid} eventKey={cap.capid}>{cap.name}</MenuItem>
              ))}
            </DropdownButton>
            <DropdownButton
              title={
                projectConnection
                  ? findProjectById(projects, projectConnection)?.name
                  : 'Select a Company Cam project'
              }
              id="project-list"
              className="integrations-dropdown"
              onSelect={(key) => setProjectConnection(key)}
            >
              {projects.map(project => (
                <MenuItem key={project.id} eventKey={project.id}>{project.name}</MenuItem>
              ))}
            </DropdownButton>
            <Button
              className="btn-brand-dark btn-compact"
              onClick={addUserIntegration}
              disabled={!capConnection || !projectConnection}
            >
              <i className="fas fa-check" />
            </Button>
          </div>
        )}

        {isCompanyCamConnected && hasActiveProjects && (
          <div className="integrations-add-row">
            <p className="integrations-add-label">Connect a CAP to a Company Cam project</p>
            <Button
              className="btn-brand-outline btn-compact"
              disabled={newConnection}
              onClick={() => setNewConnection(true)}
            >
              <i className="fas fa-plus" /> Add connection
            </Button>
          </div>
        )}

        {isCompanyCamConnected && !hasActiveProjects && (
          <p className="integrations-note">
            To connect a CAP to Company Cam, create a project at{' '}
            <a href="https://app.companycam.com/" target="_blank" rel="noopener noreferrer">
              CompanyCam
            </a>
            .
          </p>
        )}
      </section>

      {capConnection && (
        <MonitorSelectionModal
          show={showMonitorSelectionWindow}
          cap={capConnection}
          project={projectConnection}
          integration={integration || null}
          setShowMonitorSelectionWindow={setShowMonitorSelectionWindow}
        />
      )}
    </div>
  );
}
